/* Royalty-column handling for the Product Costing detail page (BM-9618).

   Pure: no React, no redux, no I/O — so the same code runs in the page and in tests. */

/** The two Landed Information columns whose percentage is driven by the costing's incoterm. */
export const ROYALTY_FEE_ALIASES = ['licenseRoyaltyFee', 'likenessRoyaltyFee'] as const;

type LandedRowLike = Record<string, unknown>;

/**
 * Clears the Licence and Likeness Royalty Fee columns ahead of an incoterm recalculation.
 *
 * The calculation chain only writes those columns when the new incoterm resolves against the
 * licence/likeness master, so without this an incoterm the master does not map would leave the
 * previous incoterm's percentage on screen while the totals silently stopped including it.
 *
 * Only the royalty columns are touched, and only where the row already carries them — a template
 * without those columns must not gain them. Every other field, including everything the user
 * entered, is copied through untouched.
 *
 * @param rows the distribution centre's landed rows.
 * @returns new rows; the input rows are not mutated.
 */
export function clearRoyaltyFees<T extends LandedRowLike>(rows: T[]): T[] {
  if (!Array.isArray(rows)) return [];
  return rows.map((row) => {
    const next = { ...row } as LandedRowLike;
    ROYALTY_FEE_ALIASES.forEach((alias) => {
      if (alias in next) next[alias] = '0';
      const calculatedKey = `${alias}CalculatedValue`;
      if (calculatedKey in next) next[calculatedKey] = '0';
    });
    return next as T;
  });
}
